import { useState } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/ui/collapsible";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/ui/tabs";
import { usePreferenciaDoPainel } from "../painel-preferencia.js";
import * as api from "../api.js";
import { confirmar, pedirTexto } from "@/componentes/pergunta.tsx";
import { useAcoes, useCasoAtual, useCelular, useEstado, useOcupado, usePode } from "../estado.jsx";
import { useNavegacao } from "../navegacao.jsx";
import { chaveDaEtiqueta } from "../etiquetas.js";
import { esperaDaConversa, pendenciaAperta, prazoEmPalavras, progressoDoFunil, sinaisDoCaso } from "../caso.js";
import { ICONES, Icone, dataHora, tinta } from "../visual.jsx";
import {
  Botao,
  Cortina,
  Etiqueta,
  Faixa,
  GradeDeWidgets,
  Gravou,
  LinhaDoPainel,
  MenuDeAcoes,
  Secao,
  CampoDeBusca,
  Widget,
} from "./comuns.jsx";
import Copiloto from "./Copiloto.jsx";
import ListaDeReunioes from "./ListaDeReunioes.jsx";
import WidgetDaPeticao from "./WidgetDaPeticao.jsx";
import CampoDaFicha from "./CampoDaFicha.jsx";
import EtiquetasRapidas, { useEtiquetasDoCaso } from "./EtiquetasDoCaso.jsx";

/*
 * A ficha do caso — uma coluna, na ordem em que se decide.
 *
 * O que ela era: nove blocos do mesmo peso, 2400px de rolagem, e as duas listas
 * de "o que fazer agora" (a do servidor e a da IA) uma embaixo da outra, com o
 * mesmo desenho e conteúdo parecido — quem abria não sabia qual clicar. Mais
 * abaixo, a linha do tempo do funil repetia ponto por ponto o trilho que a
 * conversa já desenha na largura inteira, e a FICHA imprimia a chave do banco
 * como rótulo (`funcao_cliente`) com o valor em monoespaçado terracota
 * alinhado à direita, inclusive parágrafos de sessenta palavras.
 *
 * O que ela é: AGORA (o que o servidor manda fazer, e só depois o que a IA
 * sugere), ETAPA (uma linha, que abre para mover), PENDÊNCIAS, FICHA (rótulo do
 * esquema, valor formatado, texto longo que abre), REUNIÕES e o histórico
 * fechado no pé. A identidade saiu daqui e virou o cabeçalho preso no alto.
 */

/*
 * Os estados que o servidor aceita (`CONFERENCIA`, em `src/rotas/documentos.ts`).
 * A lista é curta e fechada — é vocabulário de conferência, não campo do CRM.
 */
const CONFERENCIA = ["aprovado", "faltante", "ilegivel", "dispensado"];

/*
 * Como cada estado é DITO na tela. O valor gravado continua o do servidor.
 *
 * `recebido` existe em `src/fluxo.ts` e não está na lista de escolhas daqui —
 * por isso o `??` nos selos.
 */
const ROTULO_CONFERENCIA = {
  aprovado: "Aprovado",
  recebido: "Chegou, a conferir",
  faltante: "Está faltando",
  ilegivel: "Ilegível",
  dispensado: "Dispensado",
};

/*
 * A cor de cada estado de conferência.
 *
 * Os dois que exigem DECISÃO (chegou e ninguém disse se serve; ilegível, que é
 * pedir de novo) têm cor própria — antes caíam no mesmo cinza de `dispensado`,
 * que é o estado em que não há mais nada a fazer, e quem varria a lista pulava
 * justamente eles.
 */
const CORES_DO_ESTADO = {
  aprovado: ["color-mix(in oklab, var(--color-verde-fundo) 14%, transparent)", "var(--color-verde-fundo)"],
  recebido: ["color-mix(in oklab, var(--color-ambar) 16%, transparent)", "var(--color-ambar-escuro)"],
  faltante: ["color-mix(in oklab, var(--color-ambar) 16%, transparent)", "var(--color-ambar-escuro)"],
  ilegivel: ["color-mix(in oklab, var(--color-terra) 15%, transparent)", "var(--color-terra-escura)"],
  dispensado: ["color-mix(in oklab, var(--color-foreground) 6%, transparent)", "var(--color-tinta-suave)"],
};

/*
 * O selo de origem da pendência.
 *
 * A origem é o que diz ONDE se resolve — prazo na esteira, documento com o
 * cliente, régua na cobrança do dia. As chaves são as de `src/pendencias.ts`;
 * a cor é a mesma que a marca daquele assunto já tem na mesa.
 */
const ORIGEM = {
  prazo: ["PRAZO", "color-mix(in oklab, var(--color-terra) 15%, transparent)", "var(--color-terra-escura)"],
  tarefa: ["TAREFA", "color-mix(in oklab, var(--color-ambar) 16%, transparent)", "var(--color-ambar-escuro)"],
  regua: ["RÉGUA", "color-mix(in oklab, var(--color-azul) 15%, transparent)", "var(--color-azul)"],
  documento: ["DOCUMENTO", "color-mix(in oklab, var(--color-verde-fundo) 15%, transparent)", "var(--color-verde-fundo)"],
  reuniao: ["REUNIÃO", "color-mix(in oklab, var(--color-foreground) 8%, transparent)", "var(--color-tinta-suave)"],
};

/** O título de seção com contador — a mesma linha em toda a coluna. */
/*
 * O mapa PASSO → SEÇÃO, e por que ele não é por chave de etapa.
 *
 * O painel mostrava as sete seções abertas, na mesma ordem, em qualquer etapa:
 * 2974px de rolagem numa vista de 900, e "o que fazer" começando a 610px de
 * altura — atrás da identidade repetida, do gravador de reunião (num caso
 * parado em documentos) e do cartão da IA. Quem abre um caso não procura "as
 * informações do cliente": procura o que fazer agora.
 *
 * O que destaca a seção é a AÇÃO do passo que o servidor manda
 * (`pauta.passos[].acao`, em `src/produtividade.ts`), e não a chave da etapa. Dois
 * motivos, e o segundo é o que decide: a etapa é configurável em tempo de
 * execução (mapa por chave viraria mentira no funil que o escritório montar
 * amanhã), e o servidor JÁ decide o que importa agora — ler a etapa aqui para
 * concluir a mesma coisa criaria uma segunda verdade sobre o mesmo assunto.
 *
 * `acao` é lista fechada de código, como `gatilho` nos fluxos: ação nova entra
 * aqui, ou o painel destaca a ficha (que é o padrão e nunca é errado, só é
 * genérico).
 */
/* O ícone de cada sinal. Nome que não existe em `ICONES` derruba a TELA (o
   `Icone` faz spread do desenho), e `visual.test.jsx` lê este objeto. */
const SECAO_DO_PASSO = {
  conferir_documento: "documentos",
  aprovar_checklist: "documentos",
  cobrar_documentos: "documentos",
  aplicar_reuniao: "reunioes",
  extrair_reuniao: "reunioes",
  minutar: "peca",
  aplicar_ficha: "ficha",
  sugerir_ficha: "ficha",
  /* Confirmar a tese MOVE o caso: quem decide isso quer ver a etapa, não a ficha. */
  confirmar_tese: "etapa",
  /* `responder` e `aprovar_fila` não destacam nada no painel de propósito: um se
     resolve na conversa (à esquerda) e o outro em Automação (outra tela). */
};

/* A peça `Secao_` (título + triângulo + conteúdo dobrado) morreu em 27/08 com a
   regra "widget que aparece quando precisa, nunca colapsável". Ela era o que
   fazia o painel ter sete linhas permanentes dizendo que havia coisa dentro de
   coisa que não mudou. O conteúdo dela vive aberto no pé da coluna, e o sinal
   correspondente rola até ele. */

/* Onde a seção para ao rolar até ela: logo abaixo do cabeçalho preso, pela
   altura MEDIDA dele (`CabecalhoDoPainel` escreve a variável). Os 76px fixos de
   antes eram a altura de um cabeçalho que já não tinha 76. */
const ENCOSTO = "scroll-mt-[calc(var(--altura-cabecalho-painel,56px)_+_12px)]";

const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

/* O ladrilho do ícone de cada seção: cor de token, véu pelo modificador. */
const TOM_DO_ICONE = {
  verde: "bg-verde/12 text-verde",
  terra: "bg-terra/12 text-terra",
  azul: "bg-azul/12 text-azul",
  roxo: "bg-roxo/12 text-roxo",
  neutro: "bg-foreground/6 text-tinta-suave",
};

/**
 * Uma seção da aba Caso — UM desenho para todas (29/10).
 *
 * Eram títulos em caixa alta soltos, com setinha e "⋮" sempre à vista, e o
 * conteúdo de cada um com uma moldura própria (cartão, faixa tracejada, nada).
 * Agora toda seção é o mesmo cartão: ícone num ladrilho de cor, título em letra
 * normal, contador em pílula e a seta que dobra. O cabeçalho inteiro dobra; o
 * "⋮" (subir, descer) só aparece com o ponteiro em cima — no dedo, sempre.
 * Arrastar o cabeçalho também reordena. A escolha é da pessoa
 * (`painel-preferencia.js`).
 */
function SecaoDoPainel({
  chave,
  titulo,
  icone,
  tom = "neutro",
  contador,
  alerta = false,
  aberta,
  aoAlternar,
  posicao,
  total,
  aoMover,
  children,
}) {
  const [alvo, setAlvo] = useState(false);
  const arrastavel = (e) => e.dataTransfer.types.includes("text/x-secao");
  return (
    <section
      data-bloco={chave}
      data-secao={chave}
      className={`${ENCOSTO} mt-2.5 overflow-hidden rounded-[16px] border bg-card transition-[border-color,box-shadow] duration-[var(--t-curta)] ${
        alvo ? "border-terra/50 shadow-[0_0_0_3px_color-mix(in_oklab,var(--color-terra)_14%,transparent)]" : "border-border"
      }`}
      onDragOver={(e) => {
        if (!arrastavel(e)) return;
        e.preventDefault();
        setAlvo(true);
      }}
      onDragLeave={() => setAlvo(false)}
      onDrop={(e) => {
        setAlvo(false);
        const de = e.dataTransfer.getData("text/x-secao");
        if (de && de !== chave) aoMover(de, posicao);
      }}
    >
      <Collapsible open={aberta} onOpenChange={aoAlternar}>
        <div
          draggable
          onDragStart={(e) => {
            e.dataTransfer.setData("text/x-secao", chave);
            e.dataTransfer.effectAllowed = "move";
          }}
          className="group flex h-[48px] items-center gap-1 pr-1.5 pl-3 transition-colors duration-[var(--t-toque)] hover:bg-foreground/3"
        >
          <CollapsibleTrigger
            aria-label={`${aberta ? "Recolher" : "Mostrar"} ${titulo}`}
            className="flex h-full min-w-0 flex-1 cursor-pointer items-center gap-2.5 border-none bg-transparent p-0 text-left"
          >
            <span className={`flex size-[26px] flex-none items-center justify-center rounded-[8px] ${TOM_DO_ICONE[tom]}`}>
              <Icone de={icone} tamanho={15} />
            </span>
            <span className="min-w-0 truncate text-corpo font-semibold text-foreground">{titulo}</span>
            {contador != null ? (
              <span
                className={`flex-none rounded-full px-2 py-px text-selo font-bold tabular-nums ${
                  alerta ? "bg-ambar/14 text-ambar-escuro" : "bg-foreground/6 text-tinta-suave"
                }`}
              >
                {contador}
              </span>
            ) : null}
            <span
              aria-hidden="true"
              className={`ml-auto flex size-6 flex-none items-center justify-center text-tinta-fantasma transition-transform duration-[var(--t-curta)] ease-entra ${aberta ? "" : "-rotate-90"}`}
            >
              <Icone de={ICONES.baixo} tamanho={14} />
            </span>
          </CollapsibleTrigger>
          <MenuDeAcoes
            rotulo={`Mover a seção ${titulo}`}
            titulo="Mover ou recolher"
            largura="180px"
            classe="flex size-7 flex-none cursor-pointer items-center justify-center rounded-[8px] border-none bg-transparent text-tinta-fraca opacity-0 transition-opacity duration-[var(--t-curta)] group-hover:opacity-100 hover:bg-foreground/8 hover:text-foreground focus-visible:opacity-100 data-[popup-open]:opacity-100 pointer-coarse:opacity-100"
            itens={[
              posicao > 0 && { chave: "subir", rotulo: "Subir", icone: ICONES.cima, aoTocar: () => aoMover(chave, posicao - 1) },
              posicao < total - 1 && {
                chave: "descer",
                rotulo: "Descer",
                icone: ICONES.baixo,
                aoTocar: () => aoMover(chave, posicao + 1),
              },
              {
                chave: "dobrar",
                rotulo: aberta ? "Recolher" : "Mostrar",
                icone: ICONES.arrastar,
                aoTocar: () => aoAlternar(!aberta),
              },
            ].filter(Boolean)}
          >
            <Icone de={ICONES.mais} tamanho={14} />
          </MenuDeAcoes>
        </div>
        <CollapsibleContent className="px-3 pb-3">{children}</CollapsibleContent>
      </Collapsible>
    </section>
  );
}

/* -------------------------------------------------------------- o agora */

/**
 * Um passo do servidor: o que fazer, o que ele faz, e o botão que faz.
 *
 * O cartão inteiro é o alvo — a linha de detalhe explica a consequência, e
 * consequência é o que a pessoa lê antes de apertar. A seta à direita é o
 * afordance: sem ela, um cartão claro no meio de outros cartões claros não
 * anuncia que é clicável.
 */
/**
 * Um passo do AGORA. O PRIMEIRO é grande; os outros são linha.
 *
 * Eram todos do mesmo tamanho, e com três na tela nenhum era o próximo — a
 * pessoa lia três cartões iguais e escolhia. O servidor já devolve os passos em
 * ordem de urgência (`src/produtividade.ts`): o primeiro é a resposta à pergunta
 * "o que eu faço agora", e o desenho passa a dizer isso. Os outros continuam à
 * mão, do tamanho de uma alternativa.
 */
function Passo({ passo, ocupado, aoTocar, principal = false }) {
  if (!principal)
    return (
      /* Parece BOTÃO: borda, fundo e alvo. Era texto cinza com uma seta de 12px
         sobre fundo transparente — "Enviar o contrato para assinatura" lia como
         legenda do cartão de cima. É a mesma linha-botão da ETAPA e dos sinais
         (44, raio 14): uma linha que leva a algum lugar é UM controle. */
      <div className="mb-2">
        <LinhaDoPainel
          aoTocar={aoTocar}
          disabled={ocupado}
          data-dica={passo.detalhe}
          icone={passo.confirmar ? ICONES.cadeado : undefined}
          valor={passo.rotulo}
        />
      </div>
    );

  return (
    <button
      type="button"
      onClick={aoTocar}
      disabled={ocupado}
      data-dica={passo.detalhe}
      /* O cartão de destaque da aba: o rótulo AGORA dentro dele (era um título
         solto acima), o passo em serifa, o que ele faz, e o botão que diz que é
         para apertar. Terracota claro, largura inteira — painel com dois
         primários não tem primário. É cartão: afunda .985. */
      className="group mb-2 flex w-full cursor-pointer flex-col items-start rounded-[16px] border border-terra/25 bg-[color-mix(in_oklab,var(--color-terra)_8%,var(--color-card))] p-[15px] text-left transition-[border-color,background-color,scale] duration-[var(--t-curta)] ease-entra hover:border-terra/50 active:transform-none active:scale-[.985] disabled:cursor-wait disabled:border-dashed disabled:bg-muted"
    >
      <span className="text-selo font-bold tracking-[.1em] text-terra uppercase">Agora</span>
      <span className="mt-1.5 font-serif text-[21px] leading-[1.15] text-balance text-foreground">{passo.rotulo}</span>
      {passo.detalhe ? <span className="mt-1 text-apoio leading-[1.5] text-tinta-suave">{passo.detalhe}</span> : null}
      <span className="mt-3 inline-flex h-[34px] items-center gap-1.5 rounded-[10px] bg-terra px-3.5 text-apoio font-semibold text-sobre-terra transition-colors duration-[var(--t-toque)] group-hover:bg-terra-escura">
        {passo.confirmar ? <Icone de={ICONES.cadeado} tamanho={14} /> : null}
        Fazer agora
        <Icone de={ICONES.avancar} tamanho={14} />
      </span>
    </button>
  );
}

/** Um documento esperando decisão, com as quatro respostas a um toque. */
function ItemDoChecklist({ item, podeConferir }) {
  const { conferirDocumento } = useAcoes();
  const cores = CORES_DO_ESTADO[item.estado] ?? CORES_DO_ESTADO.dispensado;

  return (
    /* A conferência é a ação mais repetida da mesa — quatro por caso, todo dia.
       É a que mais precisa confirmar onde aconteceu. */
    <Gravou
      chave={`checklist:${item.id}`}
      classe="flex min-h-[38px] items-center justify-between gap-2.5 py-1.5 [&:not(:first-child)]:before:absolute [&:not(:first-child)]:before:inset-x-0 [&:not(:first-child)]:before:top-0 [&:not(:first-child)]:before:h-px [&:not(:first-child)]:before:bg-foreground/8"
    >
      <span className="min-w-0 flex-1 truncate text-apoio">{item.documento}</span>
      {podeConferir && item.id ? (
        <MenuDeAcoes
          rotulo={`Conferir ${item.documento}`}
          titulo="Conferir este documento"
          legenda={item.documento}
          largura="200px"
          classe="inline-flex h-6 flex-none cursor-pointer items-center rounded-etiqueta border px-[9px] text-[12px] leading-none font-semibold"
          itens={CONFERENCIA.map((estado) => ({
            chave: estado,
            rotulo: ROTULO_CONFERENCIA[estado] ?? estado,
            aoTocar: () => conferirDocumento(item.id, estado),
          }))}
          estilo={({ aberto }) =>
            `background:${cores[0]};color:${cores[1]};border-color:${aberto ? "var(--color-borda-controle)" : "transparent"}`
          }
        >
          {ROTULO_CONFERENCIA[item.estado] ?? item.estado}
        </MenuDeAcoes>
      ) : (
        <Etiqueta cores={cores}>{ROTULO_CONFERENCIA[item.estado] ?? item.estado}</Etiqueta>
      )}
    </Gravou>
  );
}

/**
 * AGORA — uma pilha só, e o servidor manda nela.
 *
 * Eram DUAS listas adjacentes com o mesmo desenho: "PRÓXIMAS AÇÕES SUGERIDAS"
 * (do modelo) e "PRÓXIMO PASSO" (do banco, com rota e permissão). Diziam quase a
 * mesma coisa com palavras diferentes — "Pedir os documentos que faltam" e
 * "Pedir o que falta" — e nada na tela dizia qual das duas executava algo. Aqui
 * o que EXECUTA vem primeiro e aberto; o que SUGERE vem depois, dobrado, dito
 * como sugestão. Quando não há passo do servidor, a sugestão abre sozinha: aí
 * ela é a única coisa que há.
 */
function Agora() {
  const { caso, ia } = useEstado();
  const ocupado = useOcupado();
  const { executarPasso, decidirFila, acaoDoCopiloto } = useAcoes();
  const { ajustar } = useNavegacao();
  const pode = usePode();
  const podeConferir = pode("documentos:conferir");
  const passos = (caso.pauta?.passos ?? []).filter((p) => pode(p.permissao));
  const fila = caso.pauta?.fila ?? [];
  const sugestoes = ia?.acoes ?? [];
  /* O passo de conferir documento não é um POST: a rota confere UM item e pede o
     estado. Quem tem o id de cada um é o checklist — o passo dá o rótulo, a
     permissão e a contagem, e a decisão acontece item por item, aqui. */
  const aConferir = passos.some((p) => p.acao === "conferir_documento")
    ? (caso.checklist ?? []).filter((i) => i.estado === "recebido")
    : [];

  if (!passos.length && !fila.length && !sugestoes.length) return null;

  return (
    /* `data-secao="agora"`: é o endereço do `?painel=agora` e da demonstração. */
    <div data-secao="agora" className={ENCOSTO}>
      {/* O PRIMEIRO passo é o grande; do segundo em diante são linhas. É a
          diferença entre "o que eu faço agora" e "o que mais dá para fazer" —
          três cartões iguais na tela não respondem a primeira pergunta. */}
      {passos.map((p, n) =>
        p.acao === "conferir_documento" ? (
          <div
            key={p.acao}
            className={
              n === 0
                ? "mb-2 rounded-[16px] border border-ambar/35 bg-ambar/10 p-[15px]"
                : "mb-2 rounded-cartao border border-ambar/34 bg-ambar/9 p-[11px_12px]"
            }
          >
            {n === 0 ? <div className="mb-1.5 text-selo font-bold tracking-[.1em] text-ambar-escuro uppercase">Agora</div> : null}
            <div className={n === 0 ? "font-serif text-[21px] leading-[1.15]" : "text-corpo font-semibold"}>{p.rotulo}</div>
            <div className="mt-[3px] text-apoio leading-[1.45] text-tinta-suave">{p.detalhe}</div>
            <div className="mt-1.5 border-t border-t-foreground/8 pt-1">
              {aConferir.map((i) => (
                <ItemDoChecklist key={i.id ?? i.documento} item={i} podeConferir={podeConferir} />
              ))}
            </div>
            {/* Decidir com o documento na tela, um por um: a pastilha ao lado do
                nome serve para a correção solta ("este eu marquei errado"); para
                conferir uma fila inteira, o lugar é a tela grande. */}
            {podeConferir && aConferir.length ? (
              <div className="mt-2">
                <Botao
                  rotulo={`Conferir ${aConferir.length} na tela`}
                  destaque
                  titulo="Abre um por um, com o arquivo grande e as três decisões"
                  aoTocar={() => ajustar({ conferir: true })}
                />
              </div>
            ) : null}
          </div>
        ) : (
          <Passo key={p.acao} passo={p} principal={n === 0} ocupado={ocupado === "passo"} aoTocar={() => executarPasso(p)} />
        ),
      )}

      {fila.map((item) => (
        <div key={item.id} className="mb-2 rounded-cartao border border-azul/30 bg-azul/8 p-[11px_12px]">
          <div className="flex items-baseline gap-2">
            <span className="min-w-0 flex-1 text-corpo font-semibold">{item.tarefa}</span>
            <Etiqueta cores={["color-mix(in oklab, var(--color-azul) 16%, transparent)", "var(--color-azul)"]} peso={700}>
              AUTOMAÇÃO
            </Etiqueta>
          </div>
          <div className="mt-1 text-apoio leading-[1.45] text-tinta-suave">
            Espera aprovação para gravar fora do CRM{item.pedido_por ? ` — pedido por ${item.pedido_por}` : ""}.
          </div>
          {pode("automacao:aprovar") ? (
            <div className="mt-2 flex gap-1.5">
              <Botao rotulo="Aprovar" titulo="Deixar o agente executar" aoTocar={() => decidirFila(item, true)} destaque />
              {/* guarda-ok: pergunta em `decidirFila` (estado.jsx) — ela sabe o nome da tarefa. */}
              <Botao rotulo="Cancelar" titulo="Tirar da fila sem executar" aoTocar={() => decidirFila(item, false)} />
            </div>
          ) : (
            <div className="mt-[7px] text-apoio text-muted-foreground">
              Quem aprova é um advogado ou sócio; o acesso se libera em Equipe e acessos.
            </div>
          )}
        </div>
      ))}

      {/*
       * As sugestões da IA: LINHAS, e só quando o servidor não tem passo.
       *
       * Eram um colapsável — "SUGESTÕES DO COPILOTO · 3", com triângulo — e ele
       * caía na regra nova: linha permanente dizendo que há coisa dentro. Pior,
       * dizia isso ACIMA do que se faz, competindo com o passo que executa.
       *
       * Com passo do servidor na tela, elas não aparecem: o passo é o que o CRM
       * MANDA fazer (tem rota e permissão) e a sugestão é o que o modelo ACHA —
       * duas listas parecidas, e a pessoa sem saber qual clicar, é o defeito que
       * a coluna já teve. Sem passo nenhum, elas são a única coisa que há, e aí
       * entram abertas.
       */}
      {sugestoes.length && !passos.length ? (
        <div className="mt-1">
          <div className="mb-1.5 flex items-center gap-1.5 text-selo font-bold tracking-[.08em] text-tinta-fraca">
            <Icone de={ICONES.brilho} tamanho={14} />O QUE A IA SUGERE
          </div>
          {sugestoes.map((acao) => (
            <button
              key={acao.titulo}
              type="button"
              onClick={() => acaoDoCopiloto(acao.marca)}
              className="lin mb-2 flex min-h-toque w-full cursor-pointer items-start gap-2 rounded-cartao border border-dashed border-borda-controle bg-transparent p-[10px_12px] text-left hover:border-tinta-fraca"
            >
              <span className="min-w-0 flex-1">
                <span className="block text-corpo leading-[1.35] font-semibold">{acao.titulo}</span>
                <span className="mt-[3px] block text-apoio leading-[1.45] text-tinta-suave">{acao.detalhe}</span>
              </span>
              <Icone de={ICONES.avancar} tamanho={16} cor="var(--color-tinta-fraca)" />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------- a etapa */

/**
 * A etapa: UMA linha que abre para mover.
 *
 * Ficava aberta na coluna — dez linhas com bolinha e "mover" — repetindo o
 * trilho do processo, que já desenha o caminho inteiro na largura toda e já
 * move. O que só existe aqui é escolher a etapa PELO NOME, e isso é decisão
 * rara: fica atrás de um toque, com o funil inteiro numa folha.
 *
 * Botões e não `RadioGroup`: no rádio a SETA muda o valor, e `onValueChange`
 * disparava a pergunta de mover a cada tecla. Mover é escrita no funil — só
 * por clique ou Enter, e sempre com `confirmar()`.
 */
function EtapaDoCaso() {
  const { etapas, caso } = useEstado();
  const { moverEtapa } = useAcoes();
  const [aberta, setAberta] = useState(false);
  const stage = caso?.registro?.stage;
  if (!stage || !etapas?.length) return null;

  const p = progressoDoFunil(etapas, stage, caso?.trilha);
  if (p.indice < 0) return null;
  const atual = p.passos[p.indice];

  const mover = async (etapa) => {
    /* A folha fecha antes da pergunta: duas caixas empilhadas disputam o foco. */
    setAberta(false);
    if (await confirmar({ titulo: `Mover para "${etapa.rotulo}"?`, acao: "Mover" })) moverEtapa(etapa.chave);
  };

  return (
    /* Mover a etapa é a escrita mais consequente da ficha: ela dispara régua,
       fila e prazo. O verde do recibo é o que confirma onde aconteceu. */
    <Gravou chave={`etapa:${caso.registro.id}`}>
      {/* O nome, há quanto tempo e a barra do funil: é a mesma barra do trilho
          da conversa, em miniatura — a pessoa já sabe lê-la. O toque abre o funil
          inteiro, onde se escolhe pelo nome. */}
      <button
        type="button"
        onClick={() => setAberta(true)}
        aria-label={`Etapa: ${atual.rotulo}${p.ha ? `, ${p.ha}` : ""}. Mover para outra etapa`}
        data-dica="Mudar a etapa do caso"
        className="group flex w-full cursor-pointer items-center gap-3 rounded-[14px] border border-border bg-card px-3 py-2.5 text-left transition-colors duration-[var(--t-toque)] hover:border-borda-controle hover:bg-foreground/3"
      >
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline justify-between gap-2">
            <span className="min-w-0 truncate text-corpo font-semibold text-foreground">{atual.rotulo}</span>
            <span className="flex-none text-apoio text-tinta-fraca tabular-nums">
              {p.ha ? `${p.ha} · ` : ""}
              {p.indice + 1} de {p.passos.length}
            </span>
          </span>
          <span className="mt-2 flex gap-[3px]" aria-hidden="true">
            {p.passos.map((etapa, i) => (
              <span
                key={etapa.chave}
                className={`h-1.5 flex-1 rounded-full ${i < p.indice ? "bg-verde" : i === p.indice ? "bg-terra" : "bg-foreground/10"}`}
              />
            ))}
          </span>
        </span>
        <Icone de={ICONES.avancar} tamanho={16} cor="var(--color-tinta-fantasma)" />
      </button>
      {aberta ? (
        <Cortina aoFechar={() => setAberta(false)} tamanho="estreita" z={44} titulo="Mover o caso de etapa">
          {p.passos.map((etapa, i) => {
            const feito = etapa.estado === "feita";
            const agora = i === p.indice;
            const bolinha = (
              <span
                className={`flex size-[14px] flex-none items-center justify-center rounded-full border-2 text-selo font-bold text-sobre-terra ${
                  agora ? "border-terra/30 bg-terra" : feito ? "border-verde bg-verde" : "border-borda-controle bg-background"
                }`}
              >
                {feito ? "✓" : ""}
              </span>
            );
            /* A etapa atual não é botão: é onde o caso já está. */
            return agora ? (
              <div
                key={etapa.chave}
                aria-current="step"
                className="flex min-h-toque items-center gap-2.5 rounded-controle px-2 py-[5px]"
              >
                {bolinha}
                <span className="min-w-0 flex-1 truncate text-corpo font-bold text-foreground">{etapa.rotulo}</span>
                {p.ha ? <span className="flex-none text-apoio text-tinta-fraca">{p.ha}</span> : null}
              </div>
            ) : (
              <button
                key={etapa.chave}
                type="button"
                onClick={() => void mover(etapa)}
                className="lin flex min-h-toque w-full cursor-pointer items-center gap-2.5 rounded-controle border-none bg-transparent px-2 py-[5px] text-left"
              >
                {bolinha}
                <span
                  className={`min-w-0 flex-1 truncate text-corpo ${feito ? "font-medium text-tinta-suave" : "text-tinta-fraca"}`}
                >
                  {etapa.rotulo}
                </span>
                <span className="flex-none text-apoio font-semibold text-tinta-fraca">mover</span>
              </button>
            );
          })}
        </Cortina>
      ) : null}
    </Gravou>
  );
}

/* --------------------------------------------------------- as pendências */

/**
 * Uma pendência do caso — prazo, documento, régua ou combinado de reunião.
 *
 * Nada é calculado aqui. `dias` e `atraso` vêm de `pendencias.listar()`, com o
 * relógio do SERVIDOR: somar data no navegador colapsava qualquer atraso na
 * palavra "vencido", e um prazo vencido há quarenta dias lia igual ao de ontem.
 *
 * O limiar do destaque continua na tela porque destaque é pintura, não regra.
 */
function Pendencia({ pendencia: p }) {
  const { concluirPendencia, conferirDocumento } = useAcoes();
  const { ir } = useNavegacao();
  const podeConferir = usePode()("documentos:conferir");
  const [rotulo] = ORIGEM[p.origem] ?? ORIGEM.reuniao;
  const aperta = pendenciaAperta(p);
  /* Meio-dia, como no servidor: a data pura em UTC virava o dia anterior aqui. */
  const vence = p.vence_em ? new Date(`${String(p.vence_em).slice(0, 10)}T12:00:00`) : null;
  const tom = p.atraso > 0 ? "terra" : aperta ? "ambar" : null;

  return (
    /* Uma LINHA da lista, e não um cartão dentro do cartão da seção: o dia num
       quadradinho (o que se procura primeiro), o que fazer, e o prazo em
       palavras com a origem e quem ficou de fazer. A cor sai do prazo. */
    <div className="flex gap-3 border-t border-t-foreground/8 py-2.5 first:border-t-0 first:pt-1">
      <span
        className={`flex w-[42px] flex-none flex-col items-center justify-center self-start rounded-[10px] py-1 ${
          tom === "terra"
            ? "bg-terra/12 text-terra"
            : tom === "ambar"
              ? "bg-ambar/12 text-ambar-escuro"
              : "bg-foreground/5 text-foreground"
        }`}
      >
        {vence ? (
          <>
            <span className="text-forte leading-none font-bold tabular-nums">{vence.getDate()}</span>
            <span className="mt-0.5 text-selo font-semibold tracking-[.06em] uppercase opacity-80">
              {MESES[vence.getMonth()]}
            </span>
          </>
        ) : (
          <span className="py-1">
            <Icone de={ICONES.relogio} tamanho={16} />
          </span>
        )}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-corpo leading-[1.35] font-semibold text-balance">{p.titulo}</div>
        <div className="mt-0.5 text-apoio leading-[1.4] text-tinta-fraca">
          <span
            className={tom === "terra" ? "font-semibold text-terra" : tom === "ambar" ? "font-semibold text-ambar-escuro" : ""}
          >
            {prazoEmPalavras(p)}
          </span>
          {` · ${String(rotulo).toLowerCase()} · ${p.para ?? "de quem pegar"}`}
        </div>
        {/* "Prazo 2026-10-29" repetia, em ISO, o dia que o quadradinho já diz. */}
        {p.detalhe && !(vence && p.origem === "tarefa") ? (
          <div className="mt-1 text-apoio leading-[1.45] text-tinta-suave">{p.detalhe}</div>
        ) : null}
        <div className="mt-2 flex flex-wrap items-center gap-1.5 empty:hidden">
          {/* Só tarefa conclui aqui: o `id` da pendência é o da tabela de origem, e
            prazo de peça se resolve na esteira — por isso o link em vez do botão. */}
          {p.origem === "tarefa" ? (
            <>
              <Botao rotulo="Concluir" tamanho="mini" titulo="Dar esta tarefa por feita" aoTocar={() => concluirPendencia(p)} />
              {/* `p.id` É o id da tarefa (contrato de `src/pendencias.ts`), então dá
                para apontar A tarefa, e não a lista. */}
              <Botao
                rotulo="No quadro"
                tamanho="mini"
                titulo="Abrir esta tarefa em Tarefas e agenda"
                aoTocar={() => ir(`/tarefas?tarefa=${p.id}`)}
              />
            </>
          ) : null}
          {p.origem === "prazo" && p.link ? (
            <Botao rotulo="Abrir a peça" tamanho="mini" titulo={p.detalhe} aoTocar={() => ir(p.link)} />
          ) : null}
          {p.origem === "reuniao" && p.link ? (
            <Botao rotulo="Ver a reunião" tamanho="mini" titulo={p.detalhe} aoTocar={() => ir(p.link)} />
          ) : null}
          {/* Documento se resolve aqui: o `id` da pendência É a linha do checklist
            (contrato de `src/pendencias.ts`). */}
          {p.origem === "documento" && podeConferir ? (
            <MenuDeAcoes
              rotulo={`Conferir ${p.titulo}`}
              titulo={p.detalhe}
              legenda={p.titulo}
              largura="200px"
              classe="inline-flex h-mini flex-none cursor-pointer items-center rounded-controle border bg-card px-2.5 text-apoio font-semibold text-tinta-media hover:bg-muted hover:text-foreground"
              itens={CONFERENCIA.map((estado) => ({
                chave: estado,
                rotulo: ROTULO_CONFERENCIA[estado] ?? estado,
                aoTocar: () => conferirDocumento(p.id, estado),
              }))}
              estilo={({ aberto }) => `border-color:${aberto ? "var(--color-terra)" : "var(--color-borda-controle)"}`}
            >
              Conferir
            </MenuDeAcoes>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- a ficha */

/** Quantos documentos do checklist já não voltam para a mão de ninguém. */
function ResumoDosDocumentos({ caso }) {
  const itens = caso.checklist ?? [];
  if (!itens.length) return null;
  const prontos = itens.filter((i) => i.estado === "aprovado" || i.estado === "dispensado").length;
  const pct = Math.round((prontos / itens.length) * 100);

  return (
    /* A barra e a conta, sem moldura: o cartão é o da seção. */
    <div className="mb-1 pt-0.5">
      <span className="flex h-1.5 overflow-hidden rounded-full bg-foreground/8">
        <span
          className="block h-full rounded-full bg-verde transition-[width] duration-[var(--t-media)] ease-movendo"
          style={{ width: `${pct}%` }}
        />
      </span>
      <span className="mt-1.5 block text-apoio text-tinta-fraca">
        {prontos} de {itens.length} resolvido{itens.length === 1 ? "" : "s"}
      </span>
    </div>
  );
}

/**
 * A ficha inteira, na ordem do esquema — e o que falta nela.
 *
 * "Ficha vazia." era beco: a minuta transforma cada campo em branco em
 * "[A CONFERIR]", e a resposta já estava no servidor (`pauta.ficha.faltam` diz
 * quais campos a peça vai furar). O aviso do que falta vale MESMO com a ficha
 * meio cheia, que é o caso comum — antes só aparecia quando ela estava vazia.
 */
function FichaDoCliente() {
  const { caso, campos: esquema } = useEstado();
  const { executarPasso } = useAcoes();
  const pode = usePode();
  /* UMA regra para a ficha inteira: ela nasce completa, menos o que o SISTEMA
     preenche e ainda está vazio. O recorte "só o que a peça usa" morreu junto com
     a gaveta — campo criado pelo escritório parecia não ter sido criado, e o
     toggle escondia justamente o campo novo. `tudo` hoje é só "mostrar também os
     automáticos em branco", para quem precisa conferir que não há nada lá. */
  const [tudo, setTudo] = useState(false);
  const [busca, setBusca] = useState("");

  const dados = caso.registro?.dados ?? {};
  const preenchido = (v) => v !== "" && v != null;
  const ordem = (chave) => (esquema ?? []).find((c) => c.chave === chave)?.ordem ?? 999;

  /* O que a PEÇA precisa e não tem. `pauta.ficha.faltam` vem do servidor com os
     campos que a minuta imprimiria como "[A CONFERIR]" — eles entram na lista
     como linha vazia, para serem preenchidos AQUI. Antes só existiam numa frase
     ("3 campos viram [A CONFERIR]") que não dava para clicar. */
  const faltam = (caso.pauta?.ficha?.faltam ?? []).filter((c) => !preenchido(dados[c]));
  const cheios = Object.keys(dados).filter((c) => preenchido(dados[c]));
  /* A ordem é a do escritório (`fields.ordem`), e não a ordem em que as chaves
     caíram no JSON. Campo livre (que não está no esquema) vai para o fim. */
  const daPeca = [...new Set([...cheios, ...faltam])].toSorted((a, b) => ordem(a) - ordem(b) || a.localeCompare(b));
  /* "Todos" é o esquema inteiro: é o que permite preencher um campo que ninguém
     nunca preencheu neste caso — antes ele simplesmente não existia na tela. */
  const todos = [...new Set([...daPeca, ...(esquema ?? []).map((c) => c.chave)])].toSorted(
    (a, b) => ordem(a) - ordem(b) || a.localeCompare(b),
  );
  /* Campo que o SISTEMA preenche (número do processo, pasta, campanha, criativo)
     só aparece quando TEM valor: a ficha aberta na coluna mostrava sete linhas
     "preencher" que ninguém preenche à mão — e cada uma delas convida a digitar
     por cima do que a integração vai gravar. Cheio, ele aparece como qualquer
     outro, porque corrigir o que a máquina errou continua sendo trabalho de
     gente. O toggle abaixo continua abrindo TUDO, inclusive esses. */
  const automatico = (chave) => Number((esquema ?? []).find((c) => c.chave === chave)?.automatico) === 1;
  /* O campo que a peça precisa aparece SEMPRE, automático ou não: a frase de
     cima diz que ele está marcado abaixo, e a marca tem de estar lá. */
  const visiveis = tudo ? todos : todos.filter((c) => preenchido(dados[c]) || !automatico(c) || faltam.includes(c));
  const passo = (caso.pauta?.passos ?? []).find(
    (p) => (p.acao === "aplicar_ficha" || p.acao === "sugerir_ficha") && pode(p.permissao),
  );

  /* A busca procura em TODOS os campos, inclusive nos que o sistema preenche: quem
     digita "processo" quer achar o número do processo, cheio ou não. */
  const doEsquema = (chave) => (esquema ?? []).find((c) => c.chave === chave);
  const rotuloDe = (chave) => doEsquema(chave)?.rotulo ?? chave.replaceAll("_", " ");
  const termo = semAcento(busca);
  const mostrados = termo ? todos.filter((c) => semAcento(rotuloDe(c)).includes(termo)) : visiveis;
  /* Grupos na ordem do primeiro campo de cada um (a ordem do escritório), e
     "Outros" sempre por último — é onde cai o campo que alguém acabou de criar. */
  const grupos = new Map();
  for (const chave of mostrados) {
    const g = doEsquema(chave)?.grupo || "Outros";
    grupos.set(g, [...(grupos.get(g) ?? []), chave]);
  }
  const ordemDosGrupos = [...grupos.keys()].toSorted((a, b) => (a === "Outros") - (b === "Outros"));

  return (
    <>
      <CampoDeBusca rotulo="Buscar campo da ficha" dica="Buscar campo" valor={busca} aoMudar={setBusca} />

      {faltam.length && !termo ? (
        /* Frase em cima, botão embaixo: lado a lado, numa coluna de 300px o
           botão comia a largura e a frase quebrava palavra por linha. */
        <Faixa
          tom="aviso"
          icone={ICONES.risco}
          titulo={faltam.length === 1 ? "1 campo falta para a peça" : `${faltam.length} campos faltam para a peça`}
          classe="mt-3"
        >
          {faltam.length === 1 ? "Vira" : "Viram"} "[A CONFERIR]" na petição se {faltam.length === 1 ? "ficar" : "ficarem"} em
          branco.
          {passo ? (
            <div className="mt-2">
              <Botao rotulo={passo.rotulo} titulo={passo.detalhe} tamanho="mini" aoTocar={() => executarPasso(passo)} destaque />
            </div>
          ) : null}
        </Faixa>
      ) : null}

      {ordemDosGrupos.map((g) => {
        const chaves = grupos.get(g);
        const noGrupo = chaves.filter((c) => preenchido(dados[c])).length;
        return (
          <div key={g}>
            <div className="mt-5 mb-2 flex items-baseline justify-between px-0.5">
              <Secao espaco="0">{g}</Secao>
              <span className="text-apoio font-semibold text-tinta-fraca tabular-nums">
                {noGrupo} de {chaves.length}
              </span>
            </div>
            <div className="rounded-[16px] border border-border bg-card px-2 py-0.5">
              {chaves.map((chave, i) => (
                <CampoDaFicha
                  key={chave}
                  chave={chave}
                  valor={dados[chave]}
                  campo={doEsquema(chave)}
                  primeiro={i === 0}
                  falta={faltam.includes(chave)}
                />
              ))}
            </div>
          </div>
        );
      })}

      {termo && !mostrados.length ? (
        <p className="mt-4 text-center text-apoio text-tinta-fraca">Nenhum campo com “{busca}”.</p>
      ) : null}
      {!termo && !mostrados.length ? (
        <p className="mt-4 text-apoio leading-[1.5] text-tinta-fraca">
          Ficha vazia. Mostre os outros campos e preencha o que a peça imprime: tese, empresa, admissão, saída e salário.
        </p>
      ) : null}

      {!termo && (todos.length > visiveis.length || tudo) ? (
        <button
          type="button"
          onClick={() => setTudo(!tudo)}
          className="mt-2 min-h-toque w-full cursor-pointer rounded-controle border-none bg-transparent text-apoio font-semibold text-tinta-suave hover:bg-foreground/4 hover:text-foreground"
        >
          {tudo
            ? "Esconder os campos que o sistema preenche"
            : `Mostrar os ${todos.length - visiveis.length} campos que o sistema preenche`}
        </button>
      ) : null}

      {/* Criar campo é ESQUEMA, e por isso fica atrás da permissão de
          configurar: quem atende preenche, quem configura inventa a linha nova. */}
      {pode("config:editar") ? (
        <CriarCampo classe="lin mt-2 flex min-h-toque w-full cursor-pointer items-center justify-center gap-1.5 rounded-[12px] border border-dashed border-borda-controle bg-transparent text-apoio font-semibold text-tinta-suave hover:text-foreground">
          <Icone de={ICONES.somar} tamanho={14} />
          Campo novo
        </CriarCampo>
      ) : null}
    </>
  );
}

/* Busca sem acento e sem caixa: "funcao" acha "Função". */
const semAcento = (t) =>
  String(t ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

/**
 * Os tipos que a ficha cria — todos os que `CampoDaFicha` sabe desenhar.
 *
 * Sim/Não é uma lista de dois que o servidor valida; a lista de opções nasce com
 * as opções digitadas na hora (a lista leva o nome do campo). A ordem é a da
 * frequência: quase todo campo do escritório é texto, data ou número.
 */
const TIPOS_QUE_A_FICHA_CRIA = [
  { id: "texto", rotulo: "Texto", detalhe: "Uma linha — nome, PIX, empresa", icone: ICONES.editar },
  { id: "texto_longo", rotulo: "Texto longo", detalhe: "Parágrafo — resumo, observação", icone: ICONES.nota },
  { id: "numero", rotulo: "Número", detalhe: "Valor, quantidade, salário", icone: ICONES.somar },
  { id: "data", rotulo: "Data", detalhe: "Digitada — dia, mês e ano, ou só o que se sabe", icone: ICONES.agenda },
  { id: "email", rotulo: "E-mail", detalhe: "Confere se é um endereço válido", icone: ICONES.email },
  { id: "telefone", rotulo: "Telefone", detalhe: "Com DDD", icone: ICONES.telefone },
  { id: "booleano", rotulo: "Sim ou não", detalhe: "Uma escolha entre as duas", icone: ICONES.confere },
  { id: "selecao", rotulo: "Lista de opções", detalhe: "Você escreve as opções agora", icone: ICONES.lista },
];

/**
 * Criar um campo do esquema SEM sair do caso.
 *
 * O escritório descobre que precisa de um campo no meio de um atendimento ("e a
 * chave PIX?"), e o caminho era sair da conversa, achar a configuração e voltar —
 * na prática o dado ia para uma nota na trilha, onde não filtra nem chega à peça.
 *
 * O TIPO primeiro e o nome depois: o tipo é um toque num menu, o nome é
 * digitação, e campo criado com o tipo errado é campo que ninguém conserta. O
 * campo vale para TODOS os casos do setor — a legenda do menu diz isso.
 * `aoCriar` recebe o campo pronto (a aba Atendimento o põe na lista dela).
 */
function CriarCampo({ classe, children, aoCriar, ancora = "bottom start" }) {
  const { avisar, carregarMesa } = useAcoes();
  const criar = async (tipo) => {
    const rotulo = await pedirTexto({
      titulo: `Campo novo: ${tipo.rotulo.toLowerCase()}`,
      texto: "O nome que vai aparecer na ficha de TODOS os casos do setor.",
      acao: tipo.id === "selecao" ? "Continuar" : "Criar o campo",
      convite: tipo.id === "email" ? "E-mail do cliente" : tipo.id === "selecao" ? "Tipo de chave PIX" : "Chave PIX",
    });
    if (!rotulo) return;
    let opcoes;
    if (tipo.id === "selecao") {
      const lista = await pedirTexto({
        titulo: `As opções de "${rotulo}"`,
        texto: "Separe por vírgula. Dá para acrescentar outras depois.",
        acao: "Criar o campo",
        convite: "CPF, Celular, E-mail, Aleatória",
      });
      opcoes = String(lista ?? "")
        .split(",")
        .map((o) => o.trim())
        .filter(Boolean);
      if (!opcoes.length) return;
    }
    try {
      const campo = await api.criarCampo({ rotulo, tipo: tipo.id, ...(opcoes ? { opcoes } : {}) });
      await aoCriar?.(campo);
      /* A mesa relê porque é ela que traz o esquema (`/api/mesa` → `campos`): sem
         isto o campo existe no banco e não aparece até alguém recarregar. */
      await carregarMesa();
      avisar(`Campo "${campo.rotulo}" criado.`);
    } catch (e) {
      avisar(e.message);
    }
  };

  return (
    <MenuDeAcoes
      rotulo="Criar um campo novo"
      titulo="Que tipo de campo?"
      legenda="Vale para todos os casos do setor"
      ancora={ancora}
      largura="272px"
      altura="380px"
      classe={classe}
      itens={TIPOS_QUE_A_FICHA_CRIA.map((t) => ({
        chave: t.id,
        rotulo: t.rotulo,
        detalhe: t.detalhe,
        icone: t.icone,
        aoTocar: () => void criar(t),
      }))}
    >
      {children}
    </MenuDeAcoes>
  );
}

/* ------------------------------------------------------------ passagens */

/** Por onde o caso passou — uma linha do tempo, a etapa marcada em terracota. */
function Passagens() {
  const { caso } = useEstado();
  const passagens = (caso?.trilha ?? []).filter((e) => e.tipo === "etapa" || e.tipo === "nota").slice(0, 8);
  if (!passagens.length) return null;

  return (
    <ol className="m-0 list-none p-0">
      {passagens.map((e, i) => (
        <li key={e.id} className="relative pb-3 pl-6 last:pb-0">
          {/* O traço vai de bolinha a bolinha: nasce embaixo desta e morre no
              topo da próxima (top 5px dela = -5px daqui). Borda no <ol> passava
              do último item e não casava com o centro da bolinha. */}
          {i < passagens.length - 1 ? (
            <span aria-hidden="true" className="absolute top-[17px] -bottom-[5px] left-[5px] w-0.5 bg-foreground/12" />
          ) : null}
          <span
            aria-hidden="true"
            className={`absolute top-[5px] left-0 size-3 rounded-full border-2 ${
              e.tipo === "etapa" ? "border-terra bg-terra" : "border-borda-controle bg-background"
            }`}
          />
          <div className="text-corpo leading-[1.45] text-tinta-media">{e.texto ?? `${e.de ?? ""} → ${e.para ?? ""}`}</div>
          <div className="text-apoio text-tinta-fraca tabular-nums">
            {dataHora(e.criado_em)}
            {e.autor && e.autor !== "sistema" ? ` · ${e.autor}` : ""}
          </div>
        </li>
      ))}
    </ol>
  );
}

/* ---------------------------------------------------------------- a coluna */

/**
 * A coluna do caso — o que fazer primeiro, e o resto a um toque.
 *
 * O que ela era: sete seções ABERTAS, na mesma ordem, em qualquer etapa —
 * copiloto, agora, etapa, pendências, ficha, reuniões, histórico. Medido em
 * 27/08 com a mesa semeada: **2974px de rolagem numa vista de 900**, e o que a
 * pessoa precisa FAZER começando a 610px de altura, atrás da identidade
 * repetida (o nome e o telefone estão na coluna ao lado, a 300px de distância),
 * de um gravador de reunião num caso parado em documentos, e de um cartão de IA
 * de 310px. Quem abre um caso não vem ler a ficha do cliente: vem descobrir o
 * que fazer.
 *
 * O que ela é, em três regras:
 *
 *   1. **AGORA é o primeiro e o único sempre aberto.** É o passo que o servidor
 *      manda, com o botão que o executa.
 *   2. **Uma seção a mais abre sozinha: a do PASSO.** `SECAO_DO_PASSO` diz qual
 *      — documentos quando há o que conferir, reuniões quando há alinhamento
 *      para aplicar, a peça quando é hora de minutar. O resto fica fechado, com
 *      a linha dizendo o que tem dentro.
 *   3. **A leitura da IA é uma linha, não um cartão.** Ela continua sendo a
 *      primeira coisa depois do que fazer (é "o que a IA está entendendo do
 *      caso"), e abre inteira num toque.
 *
 * Fechado não é escondido: cada linha fechada tem contador e resumo, e custa
 * 34px em vez de 400. O que estava a três rolagens de distância passa a estar a
 * um toque — e o que a etapa torna urgente já está aberto quando a coluna abre.
 */
/* Em que aba mora cada seção pedida pela URL (`?painel=`), pelo sinal ou pela
   demonstração; o resto (vazio, "1", "copiloto", "campos") abre em Atendimento. */
const ABA_DA_SECAO = {
  agora: "caso",
  pendencias: "caso",
  reunioes: "caso",
  documentos: "caso",
  etapa: "caso",
  espera: "caso",
  ficha: "ficha",
  historico: "ficha",
};
const abaDaSecao = (secao) => ABA_DA_SECAO[secao] ?? "atendimento";

/**
 * Os poucos campos que quem atende escreve todo dia — PIX, e-mail, o dado do
 * acidente —, na aba Atendimento.
 *
 * Quais são é escolha do ESCRITÓRIO, por setor, e não do código: a lista
 * `campos_da_frente` do esquema guarda as chaves, na ordem, e grava pela rota de
 * listas que já existe (`config:editar`). São os mesmos campos da ficha, o mesmo
 * `CampoDaFicha`: escrever aqui é escrever lá.
 */
function CamposDaFrente() {
  const { caso, campos: esquema, listas } = useEstado();
  const { carregarMesa, avisar } = useAcoes();
  const pode = usePode();
  const dados = caso.registro?.dados ?? {};
  const doEsquema = (chave) => (esquema ?? []).find((c) => c.chave === chave);
  const escolhidos = (listas?.campos_da_frente ?? []).filter(doEsquema);
  const alternar = async (chave) => {
    try {
      /* guarda-ok: pergunta — tirar o campo da aba não apaga dado: ele segue na
         ficha da aba Caso e volta com um toque no mesmo menu. */
      if (escolhidos.includes(chave)) await api.tirarDaLista("campos_da_frente", chave);
      else await api.incluirNaLista("campos_da_frente", chave, escolhidos.length + 1);
      await carregarMesa();
    } catch (e) {
      avisar(e.message);
    }
  };
  /* O campo criado AQUI já entra na aba: quem cria do lado da conversa quer
     escrever nele agora, não ir buscá-lo na ficha. */
  const incluir = (campo) => api.incluirNaLista("campos_da_frente", campo.chave, escolhidos.length + 1);
  const config = pode("config:editar");
  const acaoDoGrupo =
    "lin flex h-7 cursor-pointer items-center gap-1 rounded-[8px] border-none bg-transparent px-2 text-apoio font-semibold text-tinta-suave tracking-normal normal-case hover:bg-foreground/6 hover:text-foreground";
  const escolher = (
    <MenuDeAcoes
      rotulo="Escolher os campos desta aba"
      titulo="Campos nesta aba"
      legenda="Vale para todos os casos do setor"
      ancora="bottom end"
      largura="260px"
      altura="360px"
      classe={acaoDoGrupo}
      itens={(esquema ?? [])
        .filter((c) => !Number(c.automatico))
        .map((c) => ({
          chave: c.chave,
          rotulo: c.rotulo,
          marca: escolhidos.includes(c.chave) ? "✓" : undefined,
          aoTocar: () => void alternar(c.chave),
        }))}
    >
      <Icone de={ICONES.editar} tamanho={14} />
      escolher
    </MenuDeAcoes>
  );

  return (
    <section data-secao="campos">
      <div className="mt-6 mb-2 flex items-center gap-1">
        <Secao espaco="0">Campos</Secao>
        {/* Vazio, os dois verbos moram no próprio vazio: em cima eles repetiam. */}
        {config && escolhidos.length ? (
          <span className="ml-auto flex items-center">
            {escolher}
            <CriarCampo aoCriar={incluir} ancora="bottom end" classe={acaoDoGrupo}>
              <Icone de={ICONES.somar} tamanho={14} />
              novo
            </CriarCampo>
          </span>
        ) : null}
      </div>
      {escolhidos.length ? (
        <div className="rounded-[16px] border border-border bg-card px-2 py-0.5">
          {escolhidos.map((chave, i) => (
            <CampoDaFicha
              key={chave}
              chave={chave}
              valor={dados[chave]}
              campo={doEsquema(chave)}
              primeiro={i === 0}
              falta={(caso.pauta?.ficha?.faltam ?? []).includes(chave) && !dados[chave]}
            />
          ))}
        </div>
      ) : (
        /* Vazio que termina em botão: criar o primeiro, ou trazer um da ficha. */
        <div className="rounded-[16px] border border-dashed border-borda-controle p-4 text-center">
          <p className="m-0 text-apoio leading-[1.5] text-tinta-suave">
            {config
              ? "Ponha aqui o que a equipe escreve todo dia — PIX, e-mail, o dado do acidente."
              : "Nenhum campo escolhido para esta aba ainda. Quem configura o setor escolhe; a ficha inteira está na aba Ficha."}
          </p>
          {config ? (
            <div className="mt-3 flex justify-center gap-2">
              <CriarCampo
                aoCriar={incluir}
                ancora="bottom center"
                classe="inline-flex h-padrao cursor-pointer items-center gap-1.5 rounded-controle border-none bg-foreground px-3.5 text-apoio font-semibold text-creme hover:bg-tinta-media hover:text-creme"
              >
                <Icone de={ICONES.somar} tamanho={14} />
                Criar campo
              </CriarCampo>
              {escolher}
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
}

export default function FichaDoCaso() {
  const { caso } = useEstado();
  const atual = useCasoAtual();
  /* As etiquetas do caso e a cor de cada uma saem do mesmo gancho que o painel
     de etiquetar usa — duas leituras do catálogo é como uma delas envelhece. */
  const { minhas: tags, cores: coresDasTags } = useEtiquetasDoCaso(atual?.id);
  const { ajustar, secaoDoPainel } = useNavegacao();
  const pode = usePode();
  const celular = useCelular();
  const podeConferirDocs = pode("documentos:conferir");
  const { usuario } = useEstado();
  const pref = usePreferenciaDoPainel(usuario?.id);
  /* A aba nasce em Atendimento a cada caso aberto ("por padrão", Felipe), e o
     link que pede uma seção do caso (`?painel=ficha`, o sinal, a demonstração)
     abre na aba Caso — derivado no render, para a rolagem do `Painel` já achar
     a seção montada. */
  const volta = `${atual?.id}|${secaoDoPainel}`;
  const [vista, setVista] = useState(volta);
  const [aba, setAba] = useState(() => abaDaSecao(secaoDoPainel));
  if (vista !== volta) {
    setVista(volta);
    setAba(abaDaSecao(secaoDoPainel));
  }
  if (!atual || !caso) return null;

  const checklist = caso.checklist ?? [];
  const gravacoes = caso.gravacoes ?? [];
  /* Os passos que ESTA pessoa pode dar — o mesmo recorte do AGORA. Sem ele o
     destaque saía de um passo que o AGORA nem mostra, e a coluna escondia o
     widget de uma seção que ninguém estava resolvendo. */
  const passos = (caso.pauta?.passos ?? []).filter((p) => pode(p.permissao));
  /* Com o passo de conferir no AGORA, os documentos "chegou, a conferir" já
     estão lá, cada um com o menu de conferência: a seção não os repete. */
  const conferindoNoAgora = passos.some((p) => p.acao === "conferir_documento");
  const docsDaSecao = conferindoNoAgora ? checklist.filter((i) => i.estado !== "recebido") : checklist;
  /* O que a coluna já diz noutro lugar não entra em PENDÊNCIAS: o documento
     cobrado É a linha do checklist (mesmo id, mesmo menu), e a tarefa da reunião
     marcada é o cartão da reunião. O sino continua com as duas. */
  const haMarcada = (caso.reunioes ?? []).some((r) => r.estado === "marcada");
  const pendencias = (caso.pendencias ?? []).filter(
    (p) =>
      !(p.origem === "documento" && checklist.length) &&
      !(p.origem === "tarefa" && haMarcada && String(p.titulo ?? "").startsWith("Reunião de alinhamento — ")),
  );
  /* A seção que o PRIMEIRO passo pede — é ele que o AGORA mostra em cima, e a
     ordem dos passos no servidor já é a de urgência. Sem passo nenhum, a ficha:
     é o que se olha quando não há nada a fazer, e nunca é resposta errada. */
  const destaque = SECAO_DO_PASSO[passos[0]?.acao] ?? "ficha";

  const faltam = caso.pauta?.ficha?.faltam ?? [];
  const passagens = (caso.trilha ?? []).filter((e) => e.tipo === "etapa" || e.tipo === "nota");

  /* Os sinais em ordem de urgência (a regra é de `caso.js`, com teste). O
     primeiro só fica GRANDE quando trava — dois cartões grandes competindo é o
     defeito que esta coluna já teve, em escala menor. */
  /* `?painel=reunioes` entra na conta: a sugestão "Marcar o alinhamento" só
     aparece quando NÃO há reunião e endereça esta seção — sem isto o alvo não
     está montado e o toque não faz nada (`Painel.jsx` rola até `[data-secao]`). */
  const sinaisPara = secaoDoPainel === "reunioes" ? "reunioes" : passos.length ? destaque : null;
  const { sinais } = sinaisDoCaso({ caso: { ...caso, pendencias }, faltam, secaoDoPasso: sinaisPara });
  const destacado = sinais[0]?.u >= 80 ? sinais[0] : null;
  const espera = esperaDaConversa(caso.mensagens ?? []);
  /* Os sinais levam ao BLOCO, que está aberto logo abaixo — nada de folha por
     cima da conversa para ver o que já está na coluna. */
  const irAte = (chave) => {
    /* O sinal promete mostrar: troca para a aba da seção e abre a dobrada antes
       de rolar — dois quadros, porque a aba nova monta no primeiro. */
    setAba(abaDaSecao(chave));
    if (pref.fechada(chave)) pref.alternar(chave, true);
    requestAnimationFrame(() =>
      requestAnimationFrame(() =>
        document.querySelector(`[data-bloco="${chave}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" }),
      ),
    );
  };

  /* As seções da aba Caso; `null` é seção que este caso não tem. */
  const aConferir = docsDaSecao.filter((i) => i.estado === "recebido").length;
  const secoes = {
    documentos: checklist.length
      ? {
          titulo: "Documentos",
          icone: ICONES.documento,
          tom: "verde",
          contador: aConferir ? `${aConferir} a conferir` : `${checklist.length}`,
          alerta: aConferir > 0,
          corpo: (
            <>
              <ResumoDosDocumentos caso={caso} />
              <div>
                {docsDaSecao.map((item) => (
                  <ItemDoChecklist key={item.id ?? item.documento} item={item} podeConferir={podeConferirDocs} />
                ))}
              </div>
            </>
          ),
        }
      : null,
    pendencias: pendencias.length
      ? {
          titulo: "Prazos e tarefas",
          icone: ICONES.relogio,
          tom: "terra",
          contador: `${pendencias.length}`,
          alerta: pendencias.some((x) => x.atraso > 0),
          corpo: pendencias.map((x) => <Pendencia key={x.chave} pendencia={x} />),
        }
      : null,
    reunioes: {
      titulo: "Reuniões",
      icone: ICONES.calls,
      tom: "azul",
      contador: gravacoes.length ? `${gravacoes.length}` : null,
      corpo: <ListaDeReunioes />,
    },
  };
  const presentes = pref.ordem.filter((k) => secoes[k]);

  /* O rótulo de grupo da aba Atendimento — o mesmo `Secao` da casa. */
  const grupo = (texto, extra = "") => (
    <div className={`mb-2 flex items-center ${extra}`}>
      <Secao espaco="0">{texto}</Secao>
    </div>
  );
  const gatilho =
    "relative flex h-11 flex-none items-center justify-center gap-1.5 rounded-none border-none bg-transparent px-0 text-corpo font-semibold text-tinta-fraca shadow-none transition-colors hover:text-foreground data-active:bg-transparent data-active:text-foreground data-active:shadow-none after:absolute group-data-horizontal/tabs:after:inset-x-0 group-data-horizontal/tabs:after:-bottom-px group-data-horizontal/tabs:after:h-[2.5px] after:rounded-t-[3px] after:bg-foreground after:opacity-0 after:transition-opacity data-active:after:opacity-100";

  return (
    <Tabs value={aba} onValueChange={setAba} className="gap-0">
      {/*
       * TRÊS abas (reunião de 29/10 e o protótipo aprovado no mesmo dia).
       *
       * Atendimento é o dia a dia — etiquetas, o resumo da IA e os campos que a
       * equipe escreve. Caso é o que precisa ser feito. Ficha é o cadastro
       * inteiro. Cada aba mostra só o número que importa: passos esperando no
       * Caso, campos que furam a peça na Ficha. Sublinhado, e não pílula: a
       * pílula cinza sobre o fundo da coluna era mais um bloco na pilha.
       */}
      <div className="sticky top-[var(--altura-cabecalho-painel,56px)] z-[2] bg-secondary px-4">
        <TabsList className="h-auto w-full justify-start gap-6 rounded-none group-data-horizontal/tabs:h-auto border-b border-b-border bg-transparent p-0">
          <TabsTrigger value="atendimento" className={gatilho}>
            Atendimento
          </TabsTrigger>
          <TabsTrigger value="caso" className={gatilho}>
            Caso
            {passos.length ? (
              <span className="inline-grid h-[18px] min-w-[18px] place-items-center rounded-full bg-terra px-1.5 text-selo font-bold text-sobre-terra tabular-nums">
                {passos.length}
              </span>
            ) : null}
          </TabsTrigger>
          <TabsTrigger value="ficha" className={gatilho}>
            Ficha
            {faltam.length ? (
              <span className="inline-grid h-[18px] min-w-[18px] place-items-center rounded-full bg-ambar/16 px-1.5 text-selo font-bold text-ambar-escuro tabular-nums">
                {faltam.length}
              </span>
            ) : null}
          </TabsTrigger>
        </TabsList>
      </div>

      <TabsContent value="atendimento" className="animate-[vFade_var(--t-curta)_var(--e-entra)_both] px-4 pt-4 pb-8">
        {/*
         * ETIQUETAR — ação recorrente, endereço fixo, sempre no alto.
         *
         * A etiqueta e o "+" abrem o PAINEL ancorado (pôr, tirar, criar), não o
         * modal: o modal cobria a mesa inteira para o gesto de um toque.
         * Renomear, pintar e apagar continuam nele, pelo pé do painel.
         */}
        {grupo("Etiquetas")}
        <div className="flex flex-wrap items-center gap-1.5">
          {tags.map((t) => (
            <EtiquetasRapidas
              key={t}
              alvo={atual.id}
              rotulo={`Etiqueta ${t} — pôr, tirar ou trocar`}
              classe="lin cursor-pointer rounded-etiqueta border-none bg-transparent p-0"
            >
              <Etiqueta cores={tinta(t, coresDasTags.get(chaveDaEtiqueta(t)))}>{t}</Etiqueta>
            </EtiquetasRapidas>
          ))}
          <EtiquetasRapidas
            alvo={atual.id}
            rotulo="Pôr, tirar e criar etiqueta"
            classe="alvo lin flex h-6 cursor-pointer items-center gap-1 rounded-etiqueta border border-dashed border-borda-controle bg-transparent px-[9px] text-[12px] font-semibold text-tinta-suave hover:bg-foreground/6 hover:text-tinta-media"
          >
            <Icone de={ICONES.somar} tamanho={12} />
            etiqueta
          </EtiquetasRapidas>
        </div>

        {/* O resumo da IA embaixo das etiquetas — é onde a equipe o lê (Felipe,
            08/10): a primeira pergunta de quem abre o caso é "do que se trata". */}
        {grupo("Resumo da IA", "mt-6")}
        <Copiloto />

        <CamposDaFrente />
      </TabsContent>

      <TabsContent value="caso" className="animate-[vFade_var(--t-curta)_var(--e-entra)_both] px-4 pt-4 pb-8">
        {/* A etapa em uma linha, com a barra do funil: o que o caso É antes do
            que fazer com ele. */}
        <section data-bloco="etapa" data-secao="etapa" className={`${ENCOSTO} mb-3`}>
          <EtapaDoCaso />
        </section>

        <Agora />

        {/* O sinal que TRAVA, grande e sozinho — o que não trava já está nas
            seções logo abaixo, e a linha que só rolava até elas saiu. */}
        {destacado ? (
          <div className="mb-2 grid">
            <Widget
              titulo={destacado.titulo}
              valor={destacado.valor}
              tom={destacado.tom === "acao" ? "acao" : "ambar"}
              aoTocar={() => irAte(destacado.chave)}
              dica={destacado.apoio}
            >
              <span className="flex items-baseline gap-2 text-apoio leading-[1.35]">
                <span className="min-w-0 flex-1 text-tinta-suave">
                  {destacado.apoio}
                  {destacado.relogio ? <span className="text-tinta-fraca">{` · ${destacado.relogio}`}</span> : null}
                </span>
                <span
                  className={`flex flex-none items-center gap-1 font-semibold whitespace-nowrap ${destacado.tom === "acao" ? "text-terra" : "text-ambar-escuro"}`}
                >
                  {destacado.verbo}
                  <Icone de={ICONES.avancar} tamanho={14} />
                </span>
              </span>
            </Widget>
          </div>
        ) : null}

        <GradeDeWidgets>
          <WidgetDaPeticao />
          {/* A conversa esperando é sinal de TEMPO: "com o cliente há 22h"
              decide insistir, ligar ou devolver. */}
          {espera?.comOCliente && espera.horas >= 4 ? (
            <Widget
              icone={ICONES.relogio}
              titulo="Com o cliente"
              marca="espera"
              largo
              valor={espera.ha}
              tom="escuro"
              aoTocar={celular ? () => ajustar({ painel: 0 }) : undefined}
              dica="A última palavra foi nossa — a bola está com ele"
            >
              <span className="text-apoio leading-[1.35] text-sobre-escuro/60">sem resposta desde a nossa última mensagem</span>
            </Widget>
          ) : null}
        </GradeDeWidgets>

        {/* As seções, na ordem e com as dobras de quem usa. Seção pedida pelo
            `?painel=` abre mesmo dobrada: o link prometeu mostrar. */}
        {presentes.map((chave, posicao) => {
          const { corpo, ...cabeca } = secoes[chave];
          return (
            <SecaoDoPainel
              key={chave}
              chave={chave}
              {...cabeca}
              aberta={!pref.fechada(chave) || secaoDoPainel === chave}
              aoAlternar={(aberta) => pref.alternar(chave, aberta)}
              posicao={posicao}
              total={presentes.length}
              /* A posição é entre as seções PRESENTES neste caso; a ordem
                 guardada tem todas — traduz pelo vizinho. */
              aoMover={(qual, para) => {
                const vizinho = presentes.filter((k) => k !== qual)[para];
                const sem = pref.ordem.filter((k) => k !== qual);
                pref.mover(qual, vizinho ? sem.indexOf(vizinho) : sem.length);
              }}
            >
              {corpo}
            </SecaoDoPainel>
          );
        })}
      </TabsContent>

      <TabsContent value="ficha" className="animate-[vFade_var(--t-curta)_var(--e-entra)_both] px-4 pt-4 pb-8">
        <section data-bloco="ficha" data-secao="ficha" className={ENCOSTO}>
          <FichaDoCliente />
        </section>
        {passagens.length ? (
          <section data-bloco="historico" data-secao="historico" className={`${ENCOSTO} mt-7`}>
            <div className="mb-3 flex items-baseline justify-between px-0.5">
              <Secao espaco="0">Histórico</Secao>
              <span className="text-apoio font-semibold text-tinta-fraca tabular-nums">
                {passagens.length > 8 ? `8 de ${passagens.length}` : passagens.length}
              </span>
            </div>
            <Passagens />
          </section>
        ) : null}
      </TabsContent>
    </Tabs>
  );
}
